import React, { useState, useCallback, useMemo } from 'react';
import {
    DataGrid,
    GridColDef,
    GridRowsProp,
    GridRenderCellParams,
    ptBR,
} from "@mui/x-data-grid";
import { AcaoGrid } from "../../services/tipos";
import { FaEye, FaEdit, FaTrashAlt, FaFileExcel, FaChevronLeft, FaChevronRight, FaEllipsisV } from "react-icons/fa";
import {
    CircularProgress,
    Box,
    IconButton,
    Typography,
    useMediaQuery,
    useTheme,
    Menu,
    MenuItem,
    Select,
    Tooltip,
    Snackbar,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Checkbox,
    FormControlLabel,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
} from "@mui/material";
import * as XLSX from "xlsx";
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import ReactDOMServer from 'react-dom/server';
import styled from 'styled-components';
import { toast } from 'react-toastify';
import { IconType } from 'react-icons';
import api from '../../services/api';
import { FaFilePdf } from 'react-icons/fa';
import { Radio, RadioGroup } from '@mui/material';

interface GridProps {
    carregando: boolean;
    linhas: GridRowsProp;
    colunas: GridColDef[];
    tudoMaiusculo: boolean;
    acoesExtras?: AcaoGrid[];
    exibirAcoesDeCrud?: boolean;
    handleExclusao?: (entidade: any) => void;
    handleVisualizacao?: (entidade: any) => void;
    handleEditar?: (entidade: any) => void;
    iconeAcoes?: IconType;
    mensagemSemDados?: string;
    exportarDesabilitado?: boolean;
    densidadePadrao?: 'compact' | 'standard' | 'comfortable';
    modalExportacaoAberto?: boolean;
    abrirModalExportacao?: () => void;
    fecharModalExportacao?: () => void;
    rowCount?: number;
    paginationModel?: { page: number; pageSize: number };
    onPaginationModelChange?: (model: { page: number; pageSize: number }) => void;
    paginationMode?: 'client' | 'server';
    getFiltrosParaExportar?: () => {
        dataInicial?: Date | null;
        dataFim?: Date | null;
        status?: number | null;
        idLocacao?: string | null;
    };
    endpointExport?: string;
    // Quando definido, o export busca a base completa respeitando os filtros atuais da listagem
    // (em vez de exportar apenas os registros já carregados em `linhas`). Usado em listagens com
    // paginação server-side para que o XLSX/PDF reflita todas as páginas filtradas.
    buscarTudoParaExportar?: () => Promise<any[]>;
    tituloExportacao?: string;
    exportOptions?: {
        field: string;
        headerName: string;
        valueGetter?: (params: { row: any }) => any;
    }[];
    /**
     * Altura da linha em pixels a partir do conteúdo dela. Sem isso vale a altura padrão (60 px, 100 no
     * celular), que serve para células de até duas linhas; uma célula que empilha mais blocos (Lançamento:
     * locatário, veículo e descrição) precisa pedir mais, senão o texto vaza para as linhas vizinhas.
     */
    alturaDaLinha?: (row: any) => number;
    checkboxSelection?: boolean;
    itensSelecionados?: (string | number)[];
    onSelectionChange?: (ids: (string | number)[]) => void;
}

const StyledDataGridContainer = styled(Box)`
    position: relative;
    height: 100%;
    width: 100%;
    box-sizing: border-box;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

const TopPaginationBar = styled(Box)`
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 4px;
    padding: 6px 12px;
    background-color: #fff;
    border-bottom: 1px solid rgba(0, 0, 0, 0.06);
    flex-shrink: 0;
`;

const StyledCardContainer = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding: 16px;
    overflow-y: auto;
    height: 100%;
    width: 100%;
    &::-webkit-scrollbar {
        width: 6px;
    }
    &::-webkit-scrollbar-track {
        background: #f1f1f1;
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb {
        background: #888;
        border-radius: 10px;
    }
    &::-webkit-scrollbar-thumb:hover {
        background: #555;
    }
`;

const StyledCard = styled(Card)`
    border-radius: 12px;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
    width: 100%;
    min-height: 120px;
    border-left: 4px solid #FFF;
    margin: 8px 0;
    display: flex;
    flex-direction: column;
    &:hover {
        transform: translateY(-1px);
        box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    }
`;

const StyledCardContent = styled(CardContent)`
    display: flex;
    flex-direction: column;
    gap: 12px;
    padding: 16px;
    flex-grow: 1;
    overflow: hidden;
    min-height: 80px;
`;

const StyledCardActions = styled(CardActions)`
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    padding: 8px 16px;
    gap: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.08);
    background-color: rgba(31, 70, 126, 0.02);
    min-height: 56px;
`;

const StyledIconButton = styled(IconButton)`
    color: ${props => props.color || "grey"};
    transition: all 0.2s ease;
    &:hover {
        transform: scale(1.05);
        background-color: rgba(31, 70, 126, 0.08);
    }
`;

const StyledNoRowsOverlay = styled(Box)`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    height: 100%;
    gap: 16px;
    padding: 16px;
    text-align: center;
`;

const StyledCardField = styled(Box)`
    display: flex;
    flex-direction: column;
    gap: 4px;
    margin-bottom: 8px;
    min-height: auto;
    flex-shrink: 0;
`;

const StyledCardLabel = styled(Typography)`
    font-weight: 600;
    color: #FFF;
    font-size: 0.75rem;
    text-transform: uppercase;
    letter-spacing: 0.5px;
`;

const StyledCardValue = styled(Typography)`
    font-size: 0.875rem;
    color: #333;
    word-break: break-word;
    line-height: 1.4;
    min-height: 20px;
    padding-left: 8px;
    display: flex;
    align-items: center;
`;

const valorDaColuna = (coluna: GridColDef, row: any): any => {
    if (!coluna.valueGetter) return row[coluna.field];
    try {
        return coluna.valueGetter({ id: row.id, field: coluna.field, row, value: row[coluna.field], colDef: coluna, api: {} } as any);
    } catch {
        return row[coluna.field];
    }
};

const FormatarTudoParaMaiusculo = (colunas: GridColDef[]): GridColDef[] => {
    return colunas.map((coluna) => ({
        ...coluna,
        valueFormatter: ({ value }) =>
            typeof value === "string" ? value.toUpperCase() : value,
    }));
};

// Tipo para ações internas do CRUD
interface AcaoCrud {
    Icone: IconType;
    Texto: string;
    OnClick: (row: any) => void | undefined;
}

// Tipo unificado para todas as ações
type AcaoUnificada = AcaoCrud | AcaoGrid;

// Componente para renderizar ícone/ação
const RenderizarAcao = ({ 
    acao, 
    onClick, 
    carregando,
    disabled
}: { 
    acao: AcaoUnificada;
    onClick: (e: React.MouseEvent) => void;
    carregando: boolean;
    disabled?: boolean;
}) => (
    <StyledIconButton
        sx={{ 
            color: ('Cor' in acao ? acao.Cor : "grey") || "grey",
            ...('Imagem' in acao && acao.Imagem && {
                padding: '6px',
                '& img': {
                    width: '18px',
                    height: '18px',
                    objectFit: 'contain',
                    filter: disabled ? 'grayscale(100%) opacity(0.5)' : 'none'
                }
            })
        }}
        size="small"
        onClick={onClick}
        disabled={carregando || disabled}
    >
        {'Imagem' in acao && acao.Imagem ? (
            <img 
                src={acao.Imagem} 
                alt={acao.Texto}
                style={{
                    width: '18px',
                    height: '18px',
                    objectFit: 'contain'
                }}
            />
        ) : (
            acao.Icone && <acao.Icone />
        )}
    </StyledIconButton>
);

// Componente separado para o Menu de Ações
const MenuAcoes = ({
    acoes,
    selectedRow,
    anchorEl,
    onClose,
    carregando
}: {
    acoes: AcaoUnificada[];
    selectedRow: any;
    anchorEl: HTMLElement | null;
    onClose: () => void;
    carregando: boolean;
}) => {
    const [submenuAnchorEl, setSubmenuAnchorEl] = useState<null | HTMLElement>(null);
    const [submenuItems, setSubmenuItems] = useState<AcaoGrid[]>([]);

    const handleSubMenuClick = (event: React.MouseEvent<HTMLElement>, subMenu: AcaoGrid[]) => {
        event.stopPropagation();
        if (submenuAnchorEl && submenuAnchorEl === event.currentTarget) {
            setSubmenuAnchorEl(null);
        } else {
            setSubmenuAnchorEl(event.currentTarget);
            setSubmenuItems(subMenu);
        }
    };

    const handleSubMenuClose = () => {
        setSubmenuAnchorEl(null);
    };

    const handleActionClick = (action?: (entidade: any) => void) => {
        onClose(); // Closes the main menu
        handleSubMenuClose();
        if (action) {
            action(selectedRow);
        }
    };
    
    // When the main menu closes, ensure submenus also close
    React.useEffect(() => {
        if (!anchorEl) {
            handleSubMenuClose();
        }
    }, [anchorEl]);

    if (!selectedRow) {
        return null;
    }

    return (
        <>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={onClose}
                onClick={(e) => e.stopPropagation()}
                PaperProps={{
                    style: {
                        minWidth: 160,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        border: '1px solid rgba(0, 0, 0, 0.1)',
                        borderRadius: '6px',
                    }
                }}
                transitionDuration={150}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {acoes.map((acao, index) => {
                    const acaoGrid = acao as AcaoGrid;
                    const isDisabled = acaoGrid.Condicao ? !acaoGrid.Condicao(selectedRow) : false;
                    const tooltipText = isDisabled && acaoGrid.TooltipDisabled 
                        ? (typeof acaoGrid.TooltipDisabled === 'function' ? acaoGrid.TooltipDisabled(selectedRow) : acaoGrid.TooltipDisabled)
                        : "";

                    if (acaoGrid.SubMenu && acaoGrid.SubMenu.length > 0) {
                        const menuItem = (
                            <MenuItem
                                key={index}
                                onClick={(e) => !isDisabled && handleSubMenuClick(e, acaoGrid.SubMenu!)}
                                disabled={carregando || isDisabled}
                                sx={{ justifyContent: 'space-between' }}
                            >
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                    {acaoGrid.Icone && <acaoGrid.Icone style={{ fontSize: '0.875rem' }} />}
                                    {acaoGrid.Texto}
                                </Box>
                                <FaChevronRight style={{ fontSize: '0.7rem' }} />
                            </MenuItem>
                        );

                        if (tooltipText) {
                            return (
                                <Tooltip key={index} title={tooltipText} placement="left">
                                    <div style={{ width: '100%' }}>{menuItem}</div>
                                </Tooltip>
                            );
                        }
                        return menuItem;
                    }

                    const menuItem = (
                        <MenuItem
                            key={index}
                            data-tour={`acao-${acaoGrid.Texto?.toLowerCase().replace(/\s+/g, '-')}`}
                            onClick={() => !isDisabled && handleActionClick(acao.OnClick)}
                            disabled={carregando || isDisabled}
                            sx={{ '&:hover': { backgroundColor: 'rgba(31, 70, 126, 0.06)' }, padding: '6px 12px', fontSize: '0.875rem' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: acaoGrid.Cor || 'inherit' }}>
                                {acaoGrid.Imagem ? (
                                    <img src={acaoGrid.Imagem} alt={acaoGrid.Texto} style={{ width: '14px', height: '14px', objectFit: 'contain', filter: isDisabled ? 'grayscale(100%) opacity(0.5)' : 'none' }} />
                                ) : (
                                    acaoGrid.Icone && <acaoGrid.Icone style={{ fontSize: '0.875rem' }} />
                                )}
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{acaoGrid.Texto}</Typography>
                            </Box>
                        </MenuItem>
                    );

                    if (tooltipText) {
                        return (
                            <Tooltip key={index} title={tooltipText} placement="left">
                                <div style={{ width: '100%' }}>{menuItem}</div>
                            </Tooltip>
                        );
                    }
                    return menuItem;
                })}
            </Menu>
            <Menu
                anchorEl={submenuAnchorEl}
                open={Boolean(submenuAnchorEl)}
                onClose={handleSubMenuClose}
                onClick={(e) => e.stopPropagation()}
                anchorOrigin={{ vertical: 'top', horizontal: 'left' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                PaperProps={{ style: { boxShadow: '0 2px 8px rgba(0,0,0,0.15)', border: '1px solid rgba(0, 0, 0, 0.1)', borderRadius: '6px' } }}
            >
                {submenuItems.map((item, index) => {
                    const isDisabled = item.Condicao ? !item.Condicao(selectedRow) : false;
                    const tooltipText = isDisabled && item.TooltipDisabled 
                        ? (typeof item.TooltipDisabled === 'function' ? item.TooltipDisabled(selectedRow) : item.TooltipDisabled)
                        : "";

                    const menuItem = (
                        <MenuItem
                            key={index}
                            onClick={() => !isDisabled && handleActionClick(item.OnClick)}
                            disabled={carregando || isDisabled}
                            sx={{ '&:hover': { backgroundColor: 'rgba(31, 70, 126, 0.06)' }, padding: '6px 12px', fontSize: '0.875rem' }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', color: item.Cor || 'inherit' }}>
                                {item.Imagem ? (
                                    <img src={item.Imagem} alt={item.Texto} style={{ width: '14px', height: '14px', objectFit: 'contain', filter: isDisabled ? 'grayscale(100%) opacity(0.5)' : 'none' }} />
                                ) : (
                                    item.Icone && <item.Icone style={{ fontSize: '0.875rem' }} />
                                )}
                                <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>{item.Texto}</Typography>
                            </Box>
                        </MenuItem>
                    );

                    if (tooltipText) {
                        return (
                            <Tooltip key={index} title={tooltipText} placement="left">
                                <div style={{ width: '100%' }}>{menuItem}</div>
                            </Tooltip>
                        );
                    }
                    return menuItem;
                })}
            </Menu>
        </>
    );
};

function Grid(props: GridProps) {
    const {
        carregando,
        linhas,
        colunas,
        tudoMaiusculo,
        exibirAcoesDeCrud = true,
        acoesExtras = [],
        handleExclusao,
        handleVisualizacao,
        handleEditar,
        iconeAcoes: IconeAcoes = FaEllipsisV,
        mensagemSemDados = "Nenhum registro encontrado",
        exportarDesabilitado = false,
        densidadePadrao = 'standard',
        modalExportacaoAberto = false,
        abrirModalExportacao,
        fecharModalExportacao,
        rowCount,
        paginationModel,
        onPaginationModelChange,
        paginationMode = 'client',
        getFiltrosParaExportar,
        endpointExport,
        buscarTudoParaExportar,
        tituloExportacao,
        alturaDaLinha,
        checkboxSelection = false,
        itensSelecionados,
        onSelectionChange,
    } = props;

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [mobileMenuAnchorEl, setMobileMenuAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedRow, setSelectedRow] = useState<any>(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: '' });
    const [selectedColumns, setSelectedColumns] = useState<Record<string, boolean>>({});
    const [loadingExport, setLoadingExport] = useState(false);
    const [formatoExportacao, setFormatoExportacao] = useState<'xlsx' | 'pdf'>('xlsx');

    // Texto de uma célula renderizada como React (placa + modelo, data + hora...). Tirar só as tags colava
    // os blocos ("ABC1D23FIAT UNO", "22/10/202614:30") e deixava as entidades HTML ("&amp;") no arquivo (DL-193).
    const textoDoElemento = (elemento: React.ReactElement): string => {
        const html = ReactDOMServer.renderToStaticMarkup(elemento);
        const comEspacos = html.replace(/<\/(p|span|div|li|td|th|h[1-6])>/gi, '$& ').replace(/<br\s*\/?>/gi, ' ');
        if (typeof DOMParser === 'undefined')
            return comEspacos.replace(/<[^>]+>/g, '').replace(/\s+/g, ' ').trim();
        const texto = new DOMParser().parseFromString(comEspacos, 'text/html').body.textContent || '';
        return texto.replace(/\s+/g, ' ').trim();
    };

    const initializeSelectedColumns = useCallback(() => {
        const initialSelection: Record<string, boolean> = {};

        if (props.exportOptions && props.exportOptions.length > 0) {
            props.exportOptions.forEach(opt => {
                initialSelection[opt.field] = true;
            });
            setSelectedColumns(initialSelection);
            return;
        }

        // Campos que nunca devem aparecer na exportação (binários/anexos não fazem sentido em XLSX/PDF).
        // Garantia e recibo da manutenção vêm como URLs assinadas do storage: centenas de caracteres que
        // no PDF engolem a largura das outras colunas (DL-193).
        const camposIgnorados = new Set(['imagem', 'imagens', 'arquivo', 'arquivos', 'garantia', 'recibo']);
        const ehCampoExportavel = (campo: string) =>
            !campo.startsWith('id') && campo !== 'acoes' && !camposIgnorados.has(campo);
        const ehUrl = (valor: any) => typeof valor === 'string' && /^https?:\/\//i.test(valor);

        // Propriedade cujo valor é objeto/array (ex.: veiculo, locacao, locatario) sem coluna
        // correspondente seria serializada como "[object Object]" no XLSX. Só entra na exportação
        // automática se houver um colDef para formatá-la.
        const temColuna = (campo: string) => colunas.some(c => c.field === campo);
        const ehValorSerializavel = (valor: any) =>
            valor == null || typeof valor !== 'object' || valor instanceof Date;

        const allProperties = new Set<string>();
        linhas.forEach(row => {
            Object.keys(row).forEach(prop => {
                if (ehCampoExportavel(prop) && (temColuna(prop) || (ehValorSerializavel(row[prop]) && !ehUrl(row[prop])))) {
                    allProperties.add(prop);
                }
            });
        });

        colunas.forEach(col => {
            if (ehCampoExportavel(col.field)) {
                allProperties.add(col.field);
            }
        });

        // Marca todas como selecionadas por padrão
        Array.from(allProperties).forEach(prop => {
            initialSelection[prop] = true;
        });

        setSelectedColumns(initialSelection);
    }, [linhas, colunas, props.exportOptions]);

    const handleMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, row: any) => {
        event.stopPropagation();
        setAnchorEl(event.currentTarget);
        setSelectedRow(row);
    }, []);

    const handleMobileMenuOpen = useCallback((event: React.MouseEvent<HTMLElement>, row: any) => {
        event.stopPropagation();
        setMobileMenuAnchorEl(event.currentTarget);
        setSelectedRow(row);
    }, []);

    const handleMenuClose = useCallback(() => {
        setAnchorEl(null);
        setMobileMenuAnchorEl(null);
        setSelectedRow(null);
    }, []);

    const handleRowClick = useCallback((params: any) => {
        if (handleVisualizacao) {
            handleVisualizacao(params.row);
        }
    }, [handleVisualizacao]);

    const handleSelectionModelChange = useCallback((model: any) => {
        onSelectionChange?.(model as (string | number)[]);
    }, [onSelectionChange]);

    // Prepara as linhas no formato { headerName: valor } respeitando colunas selecionadas e
    // exportOptions/renderCell de cada coluna. Usada tanto pelo XLSX quanto pelo PDF.
    const prepararLinhasParaExportacao = useCallback((dataToExport: any[]): { headers: string[]; rows: Record<string, any>[] } => {
        const columnsToExport = Object.keys(selectedColumns).filter(col => selectedColumns[col]);
        const headersOrdenados: string[] = [];
        const seenHeaders = new Set<string>();

        const rows = dataToExport.map((linha: any) => {
            const result: Record<string, any> = {};
            columnsToExport.forEach((coluna) => {
                let header: string | undefined;
                let valor: any;

                if (props.exportOptions) {
                    const opt = props.exportOptions.find(o => o.field === coluna);
                    if (opt) {
                        valor = opt.valueGetter ? opt.valueGetter({ row: linha }) : linha[coluna];
                        header = opt.headerName;
                    }
                }

                if (header === undefined) {
                    const colDef = colunas.find(c => c.field === coluna);
                    valor = colDef?.renderCell
                        ? (() => {
                            const params: GridRenderCellParams = {
                                id: linha.id,
                                field: coluna,
                                value: linha[coluna],
                                row: linha,
                                colDef: { ...colDef, computedWidth: 100, hasBeenResized: false },
                                api: {} as any,
                                hasFocus: false,
                                tabIndex: -1,
                                rowNode: {
                                    id: linha.id,
                                    depth: 0,
                                    type: 'leaf',
                                    parent: linha.id,
                                    groupingKey: String(linha.id ?? ''),
                                },
                                cellMode: 'view',
                                isEditable: false,
                                formattedValue: linha[coluna],
                            };
                            const renderizado = colDef.renderCell(params);
                            return typeof renderizado === 'string'
                                ? renderizado
                                : React.isValidElement(renderizado)
                                    ? textoDoElemento(renderizado)
                                    : (linha[coluna] ?? '');
                        })()
                        : (linha[coluna] ?? '');
                    header = colDef?.headerName || coluna;
                }

                if (!seenHeaders.has(header!)) {
                    seenHeaders.add(header!);
                    headersOrdenados.push(header!);
                }
                result[header!] = valor;
            });
            return result;
        });

        return { headers: headersOrdenados, rows };
    }, [selectedColumns, colunas, props.exportOptions]);

    const obterDadosParaExportar = useCallback(async (): Promise<any[]> => {
        if (buscarTudoParaExportar) {
            return await buscarTudoParaExportar();
        }
        if (endpointExport && getFiltrosParaExportar) {
            const filtros = getFiltrosParaExportar();
            const response = await api.get(endpointExport, { params: filtros });
            return response.data;
        }
        return [...linhas];
    }, [buscarTudoParaExportar, endpointExport, getFiltrosParaExportar, linhas]);

    const exportar = useCallback(async () => {
        if (loadingExport) return;

        setLoadingExport(true);
        try {
            const dataToExport = await obterDadosParaExportar();

            if (!dataToExport.length) {
                setSnackbar({ open: true, message: 'Não há dados para exportar' });
                setLoadingExport(false);
                return;
            }

            const { headers, rows } = prepararLinhasParaExportacao(dataToExport);
            if (!rows.length || !headers.length) {
                setSnackbar({ open: true, message: 'Selecione ao menos uma coluna para exportar' });
                setLoadingExport(false);
                return;
            }

            const dataStr = new Date().toLocaleDateString('pt-BR').replace(/\//g, '-');
            const tituloArquivo = (tituloExportacao || 'exportacao').toLowerCase().replace(/\s+/g, '-');
            const nomeArquivo = `${tituloArquivo}-${dataStr}`;

            if (formatoExportacao === 'xlsx') {
                const ws = XLSX.utils.json_to_sheet(rows, { header: headers });
                const wb = XLSX.utils.book_new();
                XLSX.utils.book_append_sheet(wb, ws, 'Dados');
                XLSX.writeFile(wb, `${nomeArquivo}.xlsx`);
            } else {
                // PDF: landscape se tiver muitas colunas para caber melhor
                const doc = new jsPDF({ orientation: headers.length > 5 ? 'landscape' : 'portrait' });
                const titulo = tituloExportacao || 'Exportação';
                doc.setFontSize(14);
                doc.text(titulo, 14, 16);
                doc.setFontSize(9);
                doc.setTextColor(100);
                doc.text(`Gerado em ${new Date().toLocaleString('pt-BR')} — ${rows.length} registro(s)`, 14, 22);

                const body = rows.map(r => headers.map(h => {
                    const v = r[h];
                    if (v === null || v === undefined) return '';
                    return String(v);
                }));

                // Fonte menor quando há muitas colunas — senão os cabeçalhos quebram
                // caractere a caractere e a tabela fica ilegível.
                const fontePdf = headers.length > 12 ? 6.5 : 8;
                autoTable(doc, {
                    head: [headers],
                    body,
                    startY: 26,
                    theme: 'striped',
                    headStyles: { fillColor: [31, 70, 126] },
                    styles: { fontSize: fontePdf, cellPadding: 1.5, overflow: 'linebreak' },
                });

                doc.save(`${nomeArquivo}.pdf`);
            }

            setSnackbar({ open: true, message: 'Exportação concluída com sucesso' });
            fecharModalExportacao?.();
        } catch (error) {
            console.error('Erro ao exportar:', error);
            setSnackbar({ open: true, message: 'Erro ao exportar os dados' });
        } finally {
            setLoadingExport(false);
        }
    }, [loadingExport, obterDadosParaExportar, prepararLinhasParaExportacao, formatoExportacao, tituloExportacao, fecharModalExportacao]);

    const handleColumnToggle = useCallback((column: string) => {
        setSelectedColumns(prev => ({
            ...prev,
            [column]: !prev[column]
        }));
    }, []);

    const toggleAllColumns = useCallback(() => {
        const allSelected = Object.values(selectedColumns).every(Boolean);
        const newSelection: Record<string, boolean> = {};

        Object.keys(selectedColumns).forEach(column => {
            newSelection[column] = !allSelected;
        });

        setSelectedColumns(newSelection);
    }, [selectedColumns]);

    // Memoizar as ações para evitar recriação desnecessária
    const acoesBase = useMemo((): AcaoCrud[] => 
        exibirAcoesDeCrud ? [
            {
                Icone: FaEye,
                Texto: "Visualizar",
                OnClick: (row: any) => handleVisualizacao?.(row)
            },
            ...(handleEditar ? [{
                Icone: FaEdit,
                Texto: "Editar",
                OnClick: (row: any) => handleEditar(row)
            }] : []),
            // Sem handler (tela sem exclusão ou usuário sem permissão), o botão não aparece.
            ...(handleExclusao ? [{
                Icone: FaTrashAlt,
                Texto: "Excluir",
                OnClick: (row: any) => handleExclusao(row)
            }] : []),
        ] : []
    , [exibirAcoesDeCrud, handleVisualizacao, handleEditar, handleExclusao]);

    const todasAcoes = useMemo((): AcaoUnificada[] => 
        [...acoesBase, ...acoesExtras]
    , [acoesBase, acoesExtras]);

    const colunasComAcoes = useMemo(() => {
        const baseColunas = tudoMaiusculo ? FormatarTudoParaMaiusculo(colunas) : colunas;
        if (!exibirAcoesDeCrud && !acoesExtras.length) return baseColunas;

        const totalAcoes = todasAcoes.length;
        const baseWidth = Math.min(60 + (totalAcoes * 40), 200);

        return [...baseColunas, {
            field: "acoes",
            headerName: "",
            filterable: false,
            hideable: false,
            disableColumnMenu: true,
            width: baseWidth,
            sortable: false,
            renderCell: (params: GridRenderCellParams) => {
                const totalAcoes = todasAcoes.length;
                const acoesVisiveis = totalAcoes <= 3 ? todasAcoes : todasAcoes.slice(0, 3);
                const acoesMenu = totalAcoes > 3 ? todasAcoes.slice(3) : [];

                return (
                    <Box 
                        display="flex" 
                        gap={0.5} 
                        justifyContent="center" 
                        width="100%"
                        onClick={(e) => e.stopPropagation()}
                    >
                        {acoesVisiveis.map((acao, index) => {
                            const acaoGrid = acao as AcaoGrid;
                            // Safe check for row to prevent null error
                            const isDisabled = params.row && acaoGrid.Condicao ? !acaoGrid.Condicao(params.row) : false;

                            let tooltipText = acao.Texto;
                            if (isDisabled && acaoGrid.TooltipDisabled) {
                                tooltipText = typeof acaoGrid.TooltipDisabled === 'function' ? acaoGrid.TooltipDisabled(params.row) : acaoGrid.TooltipDisabled;
                            }

                            return (
                                <Tooltip title={tooltipText} key={index}>
                                    <span style={{ display: 'inline-block' }} data-tour={`acao-${acaoGrid.Texto?.toLowerCase().replace(/\s+/g, '-')}`}>
                                        <RenderizarAcao
                                            acao={acao}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                if (acao.OnClick && !isDisabled) {
                                                    acao.OnClick(params.row);
                                                }
                                            }}
                                            carregando={carregando}
                                            disabled={isDisabled}
                                        />
                                    </span>
                                </Tooltip>
                            );
                        })}

                        {acoesMenu.length > 0 && (
                            <>
                                <Tooltip title="Mais ações">
                                    <StyledIconButton
                                        data-tour="btn-menu-acoes"
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, params.row)}
                                        disabled={carregando}
                                        sx={{
                                            backgroundColor: 'rgba(31, 70, 126, 0.08)',
                                            borderRadius: '4px',
                                            width: 32,
                                            height: 32,
                                            '&:hover': {
                                                backgroundColor: 'rgba(31, 70, 126, 0.12)',
                                                transform: 'scale(1.05)',
                                            },
                                            transition: 'all 0.2s ease',
                                        }}
                                    >
                                        <IconeAcoes
                                            style={{
                                                fontSize: '0.875rem',
                                                color: theme.palette.primary.main
                                            }}
                                        />
                                    </StyledIconButton>
                                </Tooltip>
                            </>
                        )}
                    </Box>
                );
            },
        }];
    }, [tudoMaiusculo, colunas, exibirAcoesDeCrud, acoesExtras.length, todasAcoes, carregando, handleMenuOpen, theme, IconeAcoes]);

    const colunasComFiltros = useMemo(() => {
        return colunasComAcoes.map(coluna => ({
            ...coluna,
            headerClassName: 'custom-header',
            sortable: coluna.field !== 'acoes',
        }));
    }, [colunasComAcoes]);

    const colunasResponsivas = useMemo(() =>
        isMobile
            ? colunasComFiltros.map((coluna) => ({
                ...coluna,
                flex: 1,
                minWidth: 120,
                hide: coluna.field !== "acoes" && coluna.field !== colunas[0]?.field,
            }))
            : colunasComFiltros
        , [isMobile, colunasComFiltros, colunas]);

    const renderCard = (row: any) => {
        const totalAcoes = todasAcoes.length;
        const acoesVisiveis = totalAcoes <= 3 ? todasAcoes : todasAcoes.slice(0, 3);
        const acoesMenu = totalAcoes > 3 ? todasAcoes.slice(3) : [];

        return (
            <StyledCard key={row.id} sx={{ my: 1, minHeight: 'auto' }}>
                <StyledCardContent>
                    {colunas.map((coluna, index) => {
                        if (coluna.field === 'acoes') return null;

                        // Coluna calculada (ex.: Prazo da locação) só tem valor pelo valueGetter; sem ele
                        // o card mostrava "NaNd" (DL-164).
                        const valor = valorDaColuna(coluna, row);

                        const params: GridRenderCellParams = {
                            id: row.id,
                            field: coluna.field,
                            value: valor,
                            row,
                            colDef: { ...coluna, computedWidth: 100, hasBeenResized: false },
                            api: {} as any,
                            hasFocus: false,
                            tabIndex: -1,
                            rowNode: {
                                id: row.id,
                                depth: 0,
                                type: 'leaf',
                                parent: row.id,
                                groupingKey: row.id.toString(),
                            },
                            cellMode: 'view',
                            isEditable: false,
                            formattedValue: valor,
                        };

                        const valorRenderizado = coluna.renderCell
                            ? coluna.renderCell(params)
                            : (typeof valor === 'string' || typeof valor === 'number' ? valor : row[coluna.field]);

                        const valorFinal = React.isValidElement(valorRenderizado)
                            ? valorRenderizado
                            : typeof valorRenderizado === 'string' && tudoMaiusculo
                                ? valorRenderizado.toUpperCase()
                                : valorRenderizado;

                        return (
                            <StyledCardField key={index}>
                                <StyledCardLabel>
                                    {coluna.headerName || coluna.field}
                                </StyledCardLabel>
                                <StyledCardValue sx={{
                                    minHeight: '24px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis'
                                }}>
                                    {valorFinal || '-'}
                                </StyledCardValue>
                            </StyledCardField>
                        );
                    })}
                </StyledCardContent>
                <StyledCardActions sx={{
                    minHeight: '56px',
                    padding: '8px 16px',
                    flexShrink: 0
                }}>
                    {acoesVisiveis.map((acao, index) => {
                        const acaoGrid = acao as AcaoGrid;
                        const isDisabled = row && acaoGrid.Condicao ? !acaoGrid.Condicao(row) : false;

                        let tooltipText = acao.Texto;
                        if (isDisabled && acaoGrid.TooltipDisabled) {
                            tooltipText = typeof acaoGrid.TooltipDisabled === 'function' ? acaoGrid.TooltipDisabled(row) : acaoGrid.TooltipDisabled;
                        }

                        return (
                            <Tooltip title={tooltipText} key={index}>
                                <span style={{ display: 'inline-block' }} data-tour={`acao-${acaoGrid.Texto?.toLowerCase().replace(/\s+/g, '-')}`}>
                                    <RenderizarAcao
                                        acao={acao}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            if (acao.OnClick && !isDisabled) {
                                                acao.OnClick(row);
                                            }
                                        }}
                                        carregando={carregando}
                                        disabled={isDisabled}
                                    />
                                </span>
                            </Tooltip>
                        );
                    })}

                    {acoesMenu.length > 0 && (
                        <>
                            <Tooltip title="Mais ações">
                                <StyledIconButton
                                    data-tour="btn-menu-acoes"
                                    size="small"
                                    onClick={(e) => handleMobileMenuOpen(e, row)}
                                    disabled={carregando}
                                    sx={{
                                        backgroundColor: 'rgba(31, 70, 126, 0.08)',
                                        borderRadius: '4px',
                                        width: 32,
                                        height: 32,
                                        '&:hover': {
                                            backgroundColor: 'rgba(31, 70, 126, 0.12)',
                                            transform: 'scale(1.05)',
                                        },
                                        transition: 'all 0.2s ease',
                                    }}
                                >
                                    <IconeAcoes
                                        style={{
                                            fontSize: '0.875rem',
                                            color: theme.palette.primary.main
                                        }}
                                    />
                                </StyledIconButton>
                            </Tooltip>
                        </>
                    )}
                </StyledCardActions>
            </StyledCard>
        );
    };

    const allColumnsSelected = useMemo(() => {
        return Object.values(selectedColumns).every(Boolean);
    }, [selectedColumns]);

    const someColumnsSelected = useMemo(() => {
        return Object.values(selectedColumns).some(Boolean) && !allColumnsSelected;
    }, [selectedColumns, allColumnsSelected]);

    React.useEffect(() => {
        if (modalExportacaoAberto) {
            if (linhas.length == 0 && !buscarTudoParaExportar && !endpointExport) {
                toast.info("Não existem dados para exportação. Primeiro clique em 'Filtros'")
                fecharModalExportacao && fecharModalExportacao();
                return;
            }
            initializeSelectedColumns();
        }
    }, [modalExportacaoAberto, initializeSelectedColumns, linhas.length, fecharModalExportacao, buscarTudoParaExportar, endpointExport]);

    const totalRegistros = rowCount ?? linhas.length;
    const paginaAtual = paginationModel?.page ?? 0;
    const tamanhoPaginaAtual = paginationModel?.pageSize ?? 10;
    const totalPaginas = Math.max(1, Math.ceil(totalRegistros / tamanhoPaginaAtual));
    const inicioFaixa = totalRegistros === 0 ? 0 : paginaAtual * tamanhoPaginaAtual + 1;
    const fimFaixa = Math.min(totalRegistros, (paginaAtual + 1) * tamanhoPaginaAtual);
    const exibirPaginacaoTopo = !!paginationModel && !!onPaginationModelChange;

    const irParaPagina = (novaPagina: number) => {
        if (onPaginationModelChange && paginationModel) {
            onPaginationModelChange({ ...paginationModel, page: novaPagina });
        }
    };

    return (
        <StyledDataGridContainer>
            {exibirPaginacaoTopo && (
                <TopPaginationBar>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.8rem', userSelect: 'none' }}
                    >
                        Linhas por página
                    </Typography>
                    <Select
                        size="small"
                        value={tamanhoPaginaAtual}
                        onChange={(e) => onPaginationModelChange && onPaginationModelChange({ page: 0, pageSize: Number(e.target.value) })}
                        sx={{ fontSize: '0.8rem', height: 28, mr: 2, '& .MuiSelect-select': { py: 0.25 } }}
                    >
                        {[10, 25, 50].map(tamanho => (
                            <MenuItem key={tamanho} value={tamanho}>{tamanho}</MenuItem>
                        ))}
                    </Select>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.6)', fontSize: '0.8rem', mr: 1, userSelect: 'none' }}
                    >
                        {inicioFaixa}–{fimFaixa} de {totalRegistros}
                    </Typography>
                    <Tooltip title="Página anterior">
                        <span>
                            <IconButton
                                size="small"
                                disabled={paginaAtual === 0 || carregando}
                                onClick={() => irParaPagina(paginaAtual - 1)}
                                aria-label="Página anterior"
                                sx={{
                                    color: '#1F467E',
                                    '&:hover': { backgroundColor: 'rgba(31, 70, 126, 0.08)' },
                                    '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
                                }}
                            >
                                <FaChevronLeft style={{ fontSize: '0.75rem' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                    <Typography
                        variant="caption"
                        sx={{ color: 'rgba(0, 0, 0, 0.75)', fontSize: '0.8rem', fontWeight: 500, minWidth: 70, textAlign: 'center', userSelect: 'none' }}
                    >
                        {paginaAtual + 1} / {totalPaginas}
                    </Typography>
                    <Tooltip title="Próxima página">
                        <span>
                            <IconButton
                                size="small"
                                disabled={paginaAtual >= totalPaginas - 1 || carregando}
                                onClick={() => irParaPagina(paginaAtual + 1)}
                                aria-label="Próxima página"
                                sx={{
                                    color: '#1F467E',
                                    '&:hover': { backgroundColor: 'rgba(31, 70, 126, 0.08)' },
                                    '&.Mui-disabled': { color: 'rgba(0, 0, 0, 0.26)' },
                                }}
                            >
                                <FaChevronRight style={{ fontSize: '0.75rem' }} />
                            </IconButton>
                        </span>
                    </Tooltip>
                </TopPaginationBar>
            )}
            {isMobile ? (
                <Box sx={{
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    {carregando ? (
                        <Box sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            height: '100%'
                        }}>
                            <CircularProgress />
                        </Box>
                    ) : linhas.length > 0 ? (
                        <StyledCardContainer sx={{
                            padding: '16px 8px',
                            gap: '12px',
                            '& > *': {
                                flexShrink: 0
                            }
                        }}>
                            {linhas.map((row) => renderCard(row))}
                        </StyledCardContainer>
                    ) : (
                        <StyledNoRowsOverlay>
                            <Typography variant="h6" color="textSecondary">
                                {mensagemSemDados}
                            </Typography>
                        </StyledNoRowsOverlay>
                    )}
                </Box>
            ) : (
                <DataGrid
                    rowHeight={isMobile ? 100 : 60}
                    {...(alturaDaLinha ? { getRowHeight: (p: any) => Math.max(isMobile ? 100 : 60, alturaDaLinha(p.model)) } : {})}
                    rows={linhas}
                    {...(checkboxSelection
                        ? {
                            checkboxSelection: true,
                            rowSelection: true,
                            rowSelectionModel: itensSelecionados,
                            onRowSelectionModelChange: handleSelectionModelChange,
                        }
                        : { rowSelection: false })}
                    loading={carregando}
                    columnHeaderHeight={40}
                    columns={colunasResponsivas}
                    localeText={ptBR.components.MuiDataGrid.defaultProps.localeText}
                    density={isMobile ? "compact" : densidadePadrao}
                    onRowClick={handleRowClick}
                    paginationMode={paginationMode}
                    rowCount={rowCount}
                    paginationModel={paginationModel}
                    onPaginationModelChange={onPaginationModelChange}
                    pageSizeOptions={[10, 25, 50]}
                    hideFooter={exibirPaginacaoTopo}
                    sx={{
                        flex: 1,
                        minHeight: 0,
                        border: 'none',
                        '& .MuiDataGrid-main': {
                            overflowX: linhas.length > 0 ? 'auto' : 'hidden',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            borderTop: '1px',
                            overflowX: 'hidden',
                        },
                        '& .MuiDataGrid-columnHeaders': {
                            minWidth: '100%',
                            backgroundColor: '#FFF',
                            borderBottom: '2px solid #ccc',
                            borderRadius: 0,
                        },
                        '& .MuiDataGrid-columnHeader': {
                            backgroundColor: '#FFF',
                            '&:focus': {
                                outline: 'none',
                            },
                            '&:focus-within': {
                                outline: 'none',
                            },
                            '&:last-child': {
                                paddingRight: '16px',
                            },
                            '&:hover': {
                                backgroundColor: '#FFF',
                            },
                        },
                        '& .MuiDataGrid-columnHeaderTitle': {
                            fontWeight: 600,
                            color: 'black',
                            fontSize: '0.8rem',
                            letterSpacing: '0.3px',
                        },
                        '& .MuiDataGrid-columnSeparator': {
                            color: '#CBD5E0',
                        },
                        '& .MuiDataGrid-menuIcon': {
                            color: '#4A5568',
                        },
                        '& .MuiDataGrid-sortIcon': {
                            color: '#4A5568',
                        },
                        '& .MuiDataGrid-virtualScrollerContent': {
                            minWidth: '100%',
                        },
                        '& .MuiDataGrid-row': {
                            backgroundColor: '#FFFFFF',
                            borderBottom: '1px solid #E2E8F0',
                            cursor: handleVisualizacao ? 'pointer' : 'default',
                            '&:nth-of-type(even)': {
                                backgroundColor: '#F8FAFC',
                            },
                            '&:hover': {
                                backgroundColor: 'rgba(31, 70, 126, 0.07)',
                            },
                            '&.Mui-selected': {
                                backgroundColor: 'rgba(31, 70, 126, 0.12)',
                            },
                            '&.Mui-selected:hover': {
                                backgroundColor: 'rgba(31, 70, 126, 0.16)',
                            },
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: 'none',
                            color: '#1A202C',
                            fontSize: '0.84rem',
                            '&:focus': {
                                outline: 'none',
                            },
                            '&:last-child': {
                                paddingRight: '16px',
                            },
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #CBD5E0',
                            backgroundColor: '#F8FAFC',
                            minHeight: '46px',
                        },
                        '& .MuiDataGrid-columnHeadersInner': {
                            width: '100% !important',
                        },
                        '& .custom-header': {
                            color: '#333333',
                            fontWeight: 600,
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            backgroundColor: '#fff',
                            padding: '8px 16px',
                            borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
                        },
                    }}
                    slots={{
                        loadingOverlay: () => (
                            <StyledNoRowsOverlay>
                                <CircularProgress />
                                <Typography variant="body2" color="textSecondary" sx={{ mt: 2 }}>
                                    Carregando...
                                </Typography>
                            </StyledNoRowsOverlay>
                        ),
                        noRowsOverlay: () => (
                            <StyledNoRowsOverlay>
                                <Typography variant="h6" color="textSecondary">
                                    {mensagemSemDados}
                                </Typography>
                                {!exportarDesabilitado && !isMobile && (
                                    <Typography variant="body2" color="textSecondary">
                                        Tente ajustar os filtros ou adicionar novos dados
                                    </Typography>
                                )}
                            </StyledNoRowsOverlay>
                        ),
                    }}
                />
            )}

            {/* Menus de ações */}
            <MenuAcoes
                acoes={todasAcoes.slice(3)}
                selectedRow={selectedRow}
                anchorEl={anchorEl}
                onClose={handleMenuClose}
                carregando={carregando}
            />

            <MenuAcoes
                acoes={todasAcoes.slice(3)}
                selectedRow={selectedRow}
                anchorEl={mobileMenuAnchorEl}
                onClose={handleMenuClose}
                carregando={carregando}
            />

            {/* Modal de seleção de colunas para exportação */}
            <Dialog
                open={modalExportacaoAberto || false}
                onClose={fecharModalExportacao}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Exportar dados</DialogTitle>
                <DialogContent dividers>
                    {loadingExport ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 4 }}>
                            <CircularProgress />
                            <Typography sx={{ ml: 2 }}>Exportando...</Typography>
                        </Box>
                    ) : (
                        <>
                            <Box sx={{ p: 2 }}>
                                <Typography variant="subtitle2" sx={{ mb: 1 }}>Formato</Typography>
                                <RadioGroup
                                    row
                                    value={formatoExportacao}
                                    onChange={(e) => setFormatoExportacao(e.target.value as 'xlsx' | 'pdf')}
                                >
                                    <FormControlLabel
                                        value="xlsx"
                                        control={<Radio />}
                                        label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><FaFileExcel /> Planilha (XLSX)</Box>}
                                    />
                                    <FormControlLabel
                                        value="pdf"
                                        control={<Radio />}
                                        label={<Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}><FaFilePdf /> PDF</Box>}
                                    />
                                </RadioGroup>
                                {buscarTudoParaExportar && (
                                    <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 1 }}>
                                        A exportação inclui todos os registros que correspondem aos filtros atuais (todas as páginas).
                                    </Typography>
                                )}
                            </Box>
                            <Divider />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2 }}>
                                <FormControlLabel
                                    control={
                                        <Checkbox
                                            checked={allColumnsSelected}
                                            indeterminate={someColumnsSelected}
                                            onChange={toggleAllColumns}
                                        />
                                    }
                                    label={allColumnsSelected ? "Desmarcar todas" : "Marcar todas"}
                                />
                                <Typography variant="body2" color="textSecondary">
                                    {Object.values(selectedColumns).filter(Boolean).length} de {Object.keys(selectedColumns).length} colunas selecionadas
                                </Typography>
                            </Box>
                            <Divider />
                            <Box sx={{ maxHeight: '400px', overflow: 'auto' }}>
                                <List dense>
                                    {Object.keys(selectedColumns).map((column) => {
                                        let label = column;
                                        if (props.exportOptions) {
                                            const opt = props.exportOptions.find(o => o.field === column);
                                            if (opt) label = opt.headerName;
                                        }

                                        return (
                                            <ListItem key={column} disablePadding>
                                                <ListItemIcon>
                                                    <Checkbox
                                                        edge="start"
                                                        checked={selectedColumns[column]}
                                                        tabIndex={-1}
                                                        disableRipple
                                                        onChange={() => handleColumnToggle(column)}
                                                    />
                                                </ListItemIcon>
                                                <ListItemText primary={label} />
                                            </ListItem>
                                        );
                                    })}
                                </List>
                            </Box>
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={fecharModalExportacao} color="primary" disabled={loadingExport}>
                        Cancelar
                    </Button>
                    <Button
                        onClick={exportar}
                        color="primary"
                        variant="contained"
                        startIcon={formatoExportacao === 'pdf' ? <FaFilePdf /> : <FaFileExcel />}
                        disabled={
                            loadingExport ||
                            Object.values(selectedColumns).filter(Boolean).length === 0 ||
                            // Quando há buscarTudoParaExportar (paginação server-side), não bloqueia pelo array local
                            (!buscarTudoParaExportar && !endpointExport && linhas.length === 0)
                        }
                    >
                        {loadingExport ? 'Exportando...' : 'Exportar'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({ ...snackbar, open: false })}
                message={snackbar.message}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            />
        </StyledDataGridContainer>
    );
}

export default Grid;